import { computed, Ref } from 'vue';
import * as yup from 'yup';
import { LocMetadata } from '@/models/loans';
import { MaskType, numeralMasked } from '../maskingUtils';

export interface ValidationData {
	locMetadata?: Ref<LocMetadata | null>;
}

const maxOverrideAmount = 99999;

export function useUpdateLocMetadataFormFields(data: ValidationData) {
	const formFields = computed(() => {
		return [
			{
				name: 'drawEnableOverrideUntilUtc',
				label: 'Override Until',
				type: 'date',
				value: data.locMetadata?.value?.drawEnableOverrideUntilUtc,
				width: 'col-12'
			},
			{
				name: 'drawOverrideAmount',
				label: 'Override Amount',
				type: 'text',
				value: data.locMetadata?.value?.drawOverrideAmount,
				width: 'col-12',
				maskType: MaskType.CURRENCY
			}
		];
	});

	const validationSchema = computed(() => {
		return yup
			.object({
				drawEnableOverrideUntilUtc: yup
					.date()
					.nullable()
					.typeError('Override Until date is invalid')
					.test('not-in-past', 'Override Until date cannot be earlier than today', function (value) {
						if (value === undefined || value === null) {
							return true;
						}

						const today = new Date().setHours(0, 0, 0, 0);
						const input = new Date(value).setHours(0, 0, 0, 0);

						return input >= today;
					}),
				drawOverrideAmount: yup
					.string()
					.nullable()
					.typeError('Override Amount is invalid')
					.test('is-valid-number', 'Override Amount must be a valid number', function (value) {
						if (!value) {
							return true;
						}

						const sanitizedValue = value.replace(/[$,]/g, '');

						if (sanitizedValue === '') { // let both-or-neither test validate
							return true;
						}

						return /^[0-9]+$/.test(sanitizedValue);
					})
					.test(
						'is-under-max-limit',
						`Override Amount cannot exceed ${numeralMasked(maxOverrideAmount)}`,
						function (value) {
							if (!value) {
								return true;
							}

							const sanitizedValue = value.replace(/[^0-9]/g, '');

							if (sanitizedValue === '') { // let both-or-neither test validate
								return true;
							}

							const numericValue = parseFloat(sanitizedValue);

							return numericValue <= maxOverrideAmount;
						}
					)
					.test('is-above-zero', 'Override Amount cannot be less than $250', function (value) {
						if (!value) {
							return true;
						}

						const sanitizedValue = value.replace(/[^0-9]/g, '');

						if (sanitizedValue === '') { // let both-or-neither test validate
							return true;
						}

						const numericValue = parseFloat(sanitizedValue);

						return numericValue >= 250;
					})
			})
			.test('both-or-neither', 'Both date and amount are required', function (value) {
				const { drawEnableOverrideUntilUtc, drawOverrideAmount } = value;

				const sanitizedAmount = drawOverrideAmount?.toString().replace(/[$,]/g, '');

				const bothNull = drawEnableOverrideUntilUtc === null && sanitizedAmount === '';
				const bothNotNull = drawEnableOverrideUntilUtc !== null && sanitizedAmount !== '';

				return bothNull || bothNotNull;
			});
	});

	return {
		formFields,
		validationSchema
	};
}
